/**
 * What the system IS, as opposed to what its subtree adds up to.
 *
 * The other half of this page reports rollups: numbers derived from the parts
 * below. These are stated on the system itself and derived from nothing - a
 * centre of gravity, an inertia tensor, an envelope volume, a delta-V budget, a
 * pointing figure an ADCS analysis produced. Several deliberately do NOT roll
 * up, and saying so is part of the point: delta-V is a function of the mass
 * ratio and is allocated downward, which is the opposite direction from a sum.
 *
 * A STRUCTURE IS DRAWN AS A STRUCTURE. An inertia tensor is six terms about a
 * point that is three more, and nine numbers in a row of nine tiles is a screen
 * that has technically shown everything and communicated nothing. The tensor
 * gets a matrix, the vectors get their axes side by side, and the datum sits
 * with the tensor it belongs to - because a tensor without the point it is taken
 * about is not a tensor anybody can use, and separating them would let a reader
 * take one without the other.
 *
 * WHAT EXISTS comes from the registry's own field list, grouped by feature path.
 * No list of attributes lives here; an attribute added to the library appears
 * with its heading, its unit and its place with nothing on this side edited.
 */

import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { MONOSPACE } from '../../theme'
import type { AttributeGroup, AttributeLeaf } from './system-attributes'

/** The axes, in order, so a cell's position and its label cannot disagree. */
const AXES = ['x', 'y', 'z']

/** The tensor's six terms, in the order a person reads a symmetric matrix. */
const TENSOR_ORDER = ['Ixx', 'Iyy', 'Izz', 'Ixy', 'Ixz', 'Iyz']

export const NOT_STATED = 'not stated'

/**
 * One leaf's value, as text.
 *
 * `not stated` rather than a blank or a zero. Unset and zero are the same width
 * on screen and opposite in meaning: a centre of gravity nobody has stated must
 * not read as a vehicle balanced exactly on its own origin.
 */
export function leafText(leaf: AttributeLeaf): string {
  if (!leaf.quantity) return NOT_STATED
  const { value, unit, text } = leaf.quantity
  // A value BEST could not split is shown exactly as the model wrote it.
  if (value === null) return text
  return `${value.toLocaleString('en-US', { maximumFractionDigits: 6 })}${unit ? ` ${unit}` : ''}`
}

/** One label-and-value pair, the unit already on the value. */
function Leaf({ leaf }: { leaf: AttributeLeaf }) {
  const body = (
    <Box sx={{ minWidth: 0 }}>
      {leaf.label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {leaf.label}
        </Typography>
      )}
      <Typography
        variant="body1"
        sx={{
          // Tabular figures, because these sit in columns and a tensor whose
          // digits do not line up cannot be compared down its own rows.
          fontVariantNumeric: 'tabular-nums',
          color: leaf.quantity ? 'text.primary' : 'text.disabled',
          fontStyle: leaf.quantity ? 'normal' : 'italic',
        }}
      >
        {leafText(leaf)}
      </Typography>
    </Box>
  )
  return leaf.help ? (
    <Tooltip title={leaf.help} describeChild>
      {body}
    </Tooltip>
  ) : (
    body
  )
}

/**
 * A symmetric tensor as a matrix, with its datum beneath.
 *
 * Six stated terms fill nine cells - the three products of inertia each appear
 * twice, which is what symmetric MEANS - and drawing the full nine is the only
 * layout in which they are visibly off-diagonal. A list of six labelled `Ixy`
 * tells a reader nothing they did not already have to know.
 *
 * The VALUE is what repeats, not the label: the cell below the diagonal reads
 * `Iyx` and shows what `Ixy` states. Those were one string until 2026-09-02 and
 * the lower triangle carried a transposed subscript over a correct number.
 */
function TensorMatrix({ terms }: { terms: Map<string, AttributeLeaf> }) {
  // TWO different strings per cell, and conflating them was a real defect: the
  // grid used to hold one name that served as both the lookup key and the
  // caption, so the lower triangle looked up `Ixy` - correctly, that is the
  // term the model states - and then PRINTED `Ixy` in a cell that is Iyx.
  //
  // The value was right and the subscript was transposed, which is the worst
  // shape for this: an engineer reading a product of inertia off the wrong
  // position has no way to tell from the screen.
  const grid = AXES.map((down, at) =>
    AXES.map((across, column) => ({
      // Where the cell IS.
      label: `I${down}${across}`,
      // What the model STATES. Only the upper triangle is declared - six terms,
      // not nine - because the tensor is symmetric, so the mirrored cell reads
      // its partner's value.
      key: at <= column ? `I${down}${across}` : `I${across}${down}`,
    })),
  )
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 1,
        // The matrix reads as one object rather than nine cells.
        borderLeft: 2,
        borderRight: 2,
        borderColor: 'divider',
        px: 1.5,
        py: 1,
        maxWidth: 460,
      }}
    >
      {grid.flatMap((row, at) =>
        row.map(({ label, key }, column) => {
          const leaf = terms.get(key)
          return (
            <Box key={`${at}-${column}`} sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontFamily: MONOSPACE }}
              >
                {label}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  color: leaf?.quantity ? 'text.primary' : 'text.disabled',
                  fontStyle: leaf?.quantity ? 'normal' : 'italic',
                }}
              >
                {leaf ? leafText(leaf) : NOT_STATED}
              </Typography>
            </Box>
          )
        }),
      )}
    </Box>
  )
}

/**
 * One attribute group: a figure, a vector, or the tensor.
 *
 * The tensor is recognised by the terms the model declared, not by the feature
 * being called `inertia`: a group carrying Ixx and Iyy IS a tensor whatever a
 * program named it, and a group that is not gets the plain layout rather than an
 * empty matrix.
 */
export function GroupCard({ group }: { group: AttributeGroup }) {
  const byLabel = new Map(group.leaves.map((leaf) => [leaf.label, leaf]))
  const isTensor = TENSOR_ORDER.every((name) => byLabel.has(name))
  // Everything the matrix does not draw - the datum point, and anything else a
  // program nested under the same feature.
  const rest = group.leaves.filter((leaf) => !TENSOR_ORDER.includes(leaf.label))

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {group.label}
      </Typography>

      {isTensor ? (
        <Stack spacing={1.5}>
          <TensorMatrix terms={byLabel} />
          {rest.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {/* Named, because unset here has a MEANING - the library says an
                    unstated datum means the tensor is about the centre of
                    gravity - and a blank would lose it. */}
                About the point
              </Typography>
              <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                {rest.map((leaf) => (
                  <Leaf key={leaf.name} leaf={leaf} />
                ))}
              </Stack>
              {rest.every((leaf) => !leaf.quantity) && (
                <Typography variant="caption" color="text.secondary">
                  Unstated, which the library defines as about the centre of gravity.
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      ) : group.isStructured ? (
        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {group.leaves.map((leaf) => (
            <Leaf key={leaf.name} leaf={leaf} />
          ))}
        </Stack>
      ) : (
        <Typography
          variant="h6"
          sx={{
            color: group.leaves[0]?.quantity ? 'text.primary' : 'text.disabled',
            fontStyle: group.leaves[0]?.quantity ? 'normal' : 'italic',
          }}
        >
          {group.leaves[0] ? leafText(group.leaves[0]) : NOT_STATED}
        </Typography>
      )}
    </Paper>
  )
}

export default GroupCard
